import type { CameraNotice, PresenceStatus } from "./faceDetection.js";

/**
 * Candidate · what the camera-presence notice LOOKS LIKE over time.
 *
 * The notice itself (useProctoring → faceDetection.noticeFor) works and is mounted. It was still
 * missable for a structural reason no debounce change can fix: it exists ONLY while the problem
 * does, and it clears within a second or two of the candidate returning to frame — which is to say
 * it is on screen exactly during the interval when the candidate, by definition, is not looking at
 * the screen. A test session can therefore collect "No face visible" in the recruiter's timeline
 * while the candidate is told nothing they ever see.
 *
 * So the view outlives the condition: the moment presence recovers, the warning becomes a calm,
 * self-clearing confirmation that says what happened and that it costs them nothing. Nothing here
 * touches the detector's thresholds — the machine still needs its 5s / 4 consecutive readings
 * before anything is claimed. This governs only how long the RESULT stays legible.
 */

/** How long the "you're back" line lingers. Long enough to be read by someone who looked up a
 *  moment after the fact; short enough that it is gone well inside one answer. */
export const RECOVERED_LINGER_MS = 8_000;

export type CameraNoticeTone = "warn" | "ok";

export interface CameraNoticeState {
  /** Null while there is nothing to say. */
  showing: { tone: CameraNoticeTone; title: string; body: string } | null;
  /** What the live warning was about, kept so the recovery line can name the right thing. */
  from: Exclude<PresenceStatus, "ok"> | null;
  /** When a lingering "recovered" line should disappear; null when nothing is timed. */
  clearAt: number | null;
}

export const IDLE_NOTICE: CameraNoticeState = { showing: null, from: null, clearAt: null };

const RECOVERED: Record<Exclude<PresenceStatus, "ok">, { title: string; body: string }> = {
  absent: {
    title: "You're back on camera — thank you.",
    body: "We couldn't see you for a moment. It's noted as context for the recruiter, never as a mark against you.",
  },
  multiple: {
    title: "Back to just you on camera — thank you.",
    body: "A second person was in view for a moment. It's noted as context for the recruiter, never as a mark against you.",
  },
};

/**
 * Fold one observation into the view.
 *
 * @param notice what the presence machine is currently asserting, or null for "all fine".
 * @param now    the clock, injected so this stays pure.
 */
export function nextCameraNotice(
  state: CameraNoticeState,
  notice: CameraNotice | null,
  now: number,
): CameraNoticeState {
  // A live problem always wins, and re-arms the linger for whenever it resolves.
  if (notice) {
    return { showing: { tone: "warn", title: notice.title, body: notice.body }, from: notice.status, clearAt: null };
  }
  if (state.showing?.tone === "warn" && state.from) {
    return { showing: { tone: "ok", ...RECOVERED[state.from] }, from: state.from, clearAt: now + RECOVERED_LINGER_MS };
  }
  if (state.clearAt !== null && now >= state.clearAt) return IDLE_NOTICE;
  return state;
}
